import { API_PATH, BASE_PATH } from './config';
import React from 'react';
import './DailyAgenda.css'; // New CSS file for DailyAgenda
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import { getActivityDisplayTitle } from './utils/activityUtils';

const DailyAgenda = ({ selectedDate, onLogout }) => {
    const { t } = useTranslation();
    const [activities, setActivities] = React.useState([]);
    const [error, setError] = React.useState('');

    // Helper function to parse UTC time strings to Date objects
    const parseUtcToLocal = (utcString) => {
        // Assuming utcString is in "YYYY-MM-DD HH:MM:SS" format
        const [datePart, timePart] = utcString.split(' ');
        const [year, month, day] = datePart.split('-').map(Number);
        const [hours, minutes, seconds] = timePart.split(':').map(Number);

        // Create a Date object in UTC, then convert to local time
        const utcDate = new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds));
        return utcDate;
    };

    // Function to detect overlaps
    const detectOverlaps = (activities) => {
        const sortedActivities = [...activities].sort((a, b) => {
            const startA = parseUtcToLocal(a.start_time_utc).getTime();
            const startB = parseUtcToLocal(b.start_time_utc).getTime();
            return startA - startB;
        });

        return sortedActivities.map((activity, index, arr) => {
            const startCurrent = parseUtcToLocal(activity.start_time_utc).getTime();
            const endCurrent = parseUtcToLocal(activity.end_time_utc).getTime();
            let hasOverlap = false;

            for (let i = 0; i < arr.length; i++) {
                if (i === index) continue;

                const startOther = parseUtcToLocal(arr[i].start_time_utc).getTime();
                const endOther = parseUtcToLocal(arr[i].end_time_utc).getTime();

                // Check for overlap: (start1 < end2 && end1 > start2)
                if (startCurrent < endOther && endCurrent > startOther) {
                    hasOverlap = true;
                    break;
                }
            }
            return { ...activity, hasOverlap };
        });
    };

    const fetchActivities = async (date) => {
        setError('');
        const token = localStorage.getItem('token');
        if (!token) {
            setError(t('no_token_error'));
            onLogout();
            return;
        }

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const formattedDate = `${year}-${month}-${day}`;


        try {
            const response = await fetch(`${API_PATH}/activities.php?start_date=${formattedDate}&end_date=${formattedDate}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });

            const data = await response.json();

            if (response.ok) {
                const activitiesWithOverlap = detectOverlaps(data);
                setActivities(activitiesWithOverlap);
            } else {
                setError(data.message || t('fetch_activities_error'));
                if (response.status === 401) {
                    onLogout();
                }
            }
        } catch (err) {
            setError(t('server_connection_error'));
            console.error('Fetch error:', err); // Debug log
        }
    };

    React.useEffect(() => {
        if (selectedDate) {
            fetchActivities(selectedDate);
        }
    }, [selectedDate]);

    const handleGetAISuggestions = async () => {
        setError('');
        const token = localStorage.getItem('token');
        if (!token) {
            setError(t('no_token_error'));
            onLogout();
            return;
        }

        try {
            const response = await fetch(`${API_PATH}/ai-suggestions.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ activities: activities })
            });

            const data = await response.json();

            if (response.ok) {
                const newActivities = [...activities, ...data.suggestions];
                const activitiesWithOverlap = detectOverlaps(newActivities);
                setActivities(activitiesWithOverlap);
            } else {
                setError(data.message || t('failed_get_ai_suggestions'));
            }
        } catch (err) {
            setError(t('server_connection_error'));
        }
    };

    return (
        <div className="daily-agenda-container">
            <h3>{selectedDate ? t('activities_for_date', { date: selectedDate.toDateString() }) : t('no_date_selected')}</h3>
            <Button variant="contained" onClick={handleGetAISuggestions} sx={{ mb: 2 }}>
                {t('get_ai_suggestions_btn')}
            </Button>
            {error && <p style={{ color: 'red' }}>{error}</p>}
            {activities.length > 0 ? (
                <div className="timeline">
                    {activities.map(activity => (
                        <div key={activity.activity_id} className={`timeline-item ${activity.hasOverlap ? 'overlap' : ''} ${activity.is_ai_suggestion ? 'ai-suggestion' : ''}`}>
                            <span
                                className="edit-icon"
                                title={t('edit_activity_tooltip')}
                            >
                                ✏️
                            </span>
                            <div className="timeline-time">
                                {parseUtcToLocal(activity.start_time_utc).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })}
                                <br />
                                {parseUtcToLocal(activity.end_time_utc).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })}
                            </div>
                            <div className="timeline-content">
                                <strong>{getActivityDisplayTitle(activity)}</strong>
                                {activity.notes && <p>{activity.notes}</p>}
                                {activity.location && <p>{t('location_prefix')}{activity.location}</p>}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <p>{t('no_activities_day')}</p>
            )}
        </div>
    );
};

export default DailyAgenda;
